/**
 * Palette export: turn a Colorize palette into a downloadable file.
 *
 * This is the export logic that used to live behind
 * `POST https://api.colorize.design/saveFile` on the legacy VPS
 * (`src/services/colors/handleFileFormat.js`), ported so the bytes it produces
 * are the same — the one deliberate exception being SVG, which is now a designed
 * palette sheet (see utils/paletteSvg.ts) instead of the VPS's bare rows of
 * rectangles. It is deliberately **isomorphic** — no `Buffer`, no `fs`, no
 * request/response objects — so exactly one implementation serves both callers:
 *
 *   - the browser (`composables/usePaletteExport.ts`), which builds the file
 *     locally so a download costs zero network round-trips and no VPS;
 *   - the public endpoint (`server/api/saveFile.post.ts`), which keeps the
 *     legacy contract alive under the colorize.design domain for the Chrome
 *     extension and any other external caller.
 *
 * `anypalette` (~1 MB unminified) is loaded with a dynamic `import()` so the
 * text formats — CSS/SASS/SCSS/SVG, which are just string building, and the two
 * the download modal puts front and center — never pay for it.
 */

import { buildPaletteSvg, type PaletteGroup } from './paletteSvg'

/** One swatch as the color API returns it. */
export interface PaletteColor {
  color: string
  group?: string
  name: string
}

/**
 * The palette payload: colors grouped by name, exactly the shape of
 * `assets/js/colorData.js` / the `/api/getColors` response.
 */
export interface PaletteData {
  nearestColors?: Record<string, PaletteColor[]>
  visualPalette?: Record<string, PaletteColor[]>
  [key: string]: unknown
}

export interface PaletteFile {
  /** Text formats return a string; palette formats return raw bytes. */
  content: string | Uint8Array
  /** File extension, no leading dot. */
  extension: string
  /** Content-Type for the download / HTTP response. */
  mimeType: string
  /** True when `content` is a Uint8Array. */
  binary: boolean
}

/**
 * Every format the download modal offers, keyed by the format token the UI and
 * the legacy API both use. Those tokens are analytics keys and a public API
 * contract — never rename one.
 *
 * `writer` is the name of the `AnyPalette.formats.*` entry used to encode it;
 * the four text formats are built by hand below and carry no writer.
 *
 * NOTE on ADOBE_COLOR_SWATCH: the VPS mapped it to ADOBE_COLOR_BOOK_PALETTE
 * (.acb), which anypalette can only *read* — `writePalette` threw
 * "format.write is not a function", so that download has been failing. The UI
 * has always labelled it "Adobe Color Swatch" and named the file `.aco`, so it
 * maps to ADOBE_COLOR_SWATCH_PALETTE here, which is the format that actually
 * matches the name and does encode.
 */
export const PALETTE_FORMATS = {
  CSS_VARIABLES: { extension: 'css', mimeType: 'text/css; charset=utf-8' },
  SASS_VARIABLES: { extension: 'sass', mimeType: 'text/plain; charset=utf-8' },
  SCSS_VARIABLES: { extension: 'scss', mimeType: 'text/plain; charset=utf-8' },
  SVG: { extension: 'svg', mimeType: 'image/svg+xml; charset=utf-8' },
  SKETCH_PALETTE: {
    extension: 'sketchpalette',
    mimeType: 'application/json; charset=utf-8',
    writer: 'SKETCH_JSON_PALETTE',
  },
  GIMP_PALETTE: {
    extension: 'gpl',
    mimeType: 'text/plain; charset=utf-8',
    writer: 'GIMP_PALETTE',
  },
  ADOBE_SWATCH_EXCHANGE: {
    extension: 'ase',
    mimeType: 'application/octet-stream',
    writer: 'ADOBE_SWATCH_EXCHANGE_PALETTE',
  },
  ADOBE_COLOR_SWATCH: {
    extension: 'aco',
    mimeType: 'application/octet-stream',
    writer: 'ADOBE_COLOR_SWATCH_PALETTE',
  },
  ADOBE_COLOR_TABLE_PALETTE: {
    extension: 'act',
    mimeType: 'application/octet-stream',
    writer: 'ADOBE_COLOR_TABLE_PALETTE',
  },
  PAINTDOTNET_PALETTE: {
    extension: 'txt',
    mimeType: 'text/plain; charset=utf-8',
    writer: 'PAINTDOTNET_PALETTE',
  },
  PAINT_SHOP_PRO_PALETTE: {
    extension: 'pal',
    mimeType: 'text/plain; charset=utf-8',
    writer: 'PAINT_SHOP_PRO_PALETTE',
  },
  HOMESITE_PALETTE: {
    extension: 'hpl',
    mimeType: 'text/plain; charset=utf-8',
    writer: 'HOMESITE_PALETTE',
  },
  SKENCIL_PALETTE: {
    extension: 'spl',
    mimeType: 'text/plain; charset=utf-8',
    writer: 'SKENCIL_PALETTE',
  },
  STAROFFICE_PALETTE: {
    extension: 'soc',
    mimeType: 'application/xml; charset=utf-8',
    writer: 'STAROFFICE_PALETTE',
  },
} as const satisfies Record<
  string,
  { extension: string; mimeType: string; writer?: string }
>

export type PaletteFormat = keyof typeof PALETTE_FORMATS

/** The format tokens `buildPaletteFile` accepts, for docs and error messages. */
export const SUPPORTED_FORMATS = Object.keys(PALETTE_FORMATS) as PaletteFormat[]

export function isPaletteFormat(format: unknown): format is PaletteFormat {
  return typeof format === 'string' && format in PALETTE_FORMATS
}

/* -------------------------------------------------------------------------- */
/* Filenames                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Downloads used to land as a bare `palette.css`, which says nothing about who
 * made it or which site it came from — and collides into `palette (3).css` the
 * moment you export two sites. Exports are named
 * `colorize.design-<site>-palette.<ext>` instead: the brand first, then the
 * analysed domain, with the format carried by the extension.
 */
const FILENAME_BRAND = 'colorize.design'

/**
 * Extensions that don't name their own format get the tool spelled out, so a
 * PAINT.NET palette isn't just another `.txt` in the Downloads folder. Every
 * other extension here is unique to one tool and identifies itself.
 */
const FILENAME_FORMAT_HINTS: Partial<Record<PaletteFormat, string>> = {
  PAINTDOTNET_PALETTE: 'paint-net',
}

/**
 * The analysed site reduced to a filename-safe label:
 * `https://www.Stripe.com/pricing?x=1` → `stripe.com`. Anything that isn't
 * URL-shaped is sanitised as-is, and an empty result simply drops out of the
 * name.
 */
export function paletteSourceSlug(source: unknown): string {
  if (typeof source !== 'string') return ''
  const trimmed = source.trim()
  if (!trimmed) return ''

  let host = trimmed
  try {
    // The site is usually typed without a scheme ("stripe.com"), which `URL`
    // rejects, so assume https when there is no scheme of its own.
    host = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`).hostname
  } catch {
    // Not a URL — fall through with the raw string.
  }

  return host
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/[^a-z0-9.-]+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 60)
}

/**
 * Base name (no extension) for an exported palette, e.g.
 * `colorize.design-stripe.com-palette`. Falls back to `colorize.design-palette`
 * when the source site is unknown.
 */
export function paletteFileBaseName(format: string, source?: unknown): string {
  const hint = isPaletteFormat(format) ? FILENAME_FORMAT_HINTS[format] : undefined
  return [FILENAME_BRAND, paletteSourceSlug(source), hint, 'palette'].filter(Boolean).join('-')
}

/** Full download name, e.g. `colorize.design-stripe.com-palette.css`. */
export function paletteFileName(format: string, source?: unknown, extension?: string): string {
  const ext =
    extension || (isPaletteFormat(format) ? PALETTE_FORMATS[format].extension : 'txt')
  return `${paletteFileBaseName(format, source)}.${ext}`
}

/** Thrown for anything the caller can fix: bad format token, unparseable data. */
export class PaletteExportError extends Error {
  statusCode: number
  constructor(message: string, statusCode = 400) {
    super(message)
    this.name = 'PaletteExportError'
    this.statusCode = statusCode
  }
}

/**
 * Walk `nearestColors` then `visualPalette` in order, handing each non-empty
 * group to `visit`. Both branches of the original did exactly this, and the
 * order decides the order of the swatches in the file.
 */
function forEachGroup(
  data: PaletteData,
  visit: (group: string, colors: PaletteColor[]) => void,
) {
  for (const section of [data.nearestColors, data.visualPalette]) {
    if (!section) continue
    for (const [group, colors] of Object.entries(section)) {
      if (Array.isArray(colors) && colors.length > 0) visit(group, colors)
    }
  }
}

/** CSS custom properties / SASS / SCSS variables. */
function buildVariables(data: PaletteData, format: PaletteFormat): string {
  const isCSS = format === 'CSS_VARIABLES'
  const prefix = isCSS ? '--' : '$'
  let out = isCSS ? ':root {\n' : ''

  forEachGroup(data, (group, colors) => {
    out += `${isCSS ? '/* ' : '// '}${group}${isCSS ? ' */\n' : '\n'}`
    for (const color of colors) {
      out += `${prefix}${color.name}: ${color.color};\n`
    }
  })

  if (isCSS) out += '}\n'
  return out
}

/** The palette flattened to the titled groups the SVG sheet lays out. */
function collectGroups(data: PaletteData): PaletteGroup[] {
  const groups: PaletteGroup[] = []
  forEachGroup(data, (name, colors) => groups.push({ name, colors }))
  return groups
}

/**
 * Encode via anypalette. It parses the palette JSON as *text*, scanning it for
 * color literals — that is how the VPS did it, and it is what makes formats
 * like `.ase` pick up `rgba()` values the structured walk above would have to
 * convert by hand.
 *
 * The `import()` is deliberate: it keeps the ~1 MB encoder out of the initial
 * payload on both sides, and every bundler gets it through the sloppy-mode
 * patch in build/anypalette-strict-fix.mjs, without which this throws.
 */
async function buildWithAnyPalette(text: string, writer: string): Promise<Uint8Array | string> {
  const mod: any = await import('anypalette')
  const AnyPalette = mod?.loadPalette ? mod : mod.default

  const palette = await new Promise<any>((resolve, reject) => {
    AnyPalette.loadPalette({ data: text }, (error: unknown, loaded: unknown) => {
      if (error || !loaded) {
        reject(new PaletteExportError('Failed to load palette from the supplied data.'))
        return
      }
      resolve(loaded)
    })
  })

  const content = AnyPalette.writePalette(palette, AnyPalette.formats[writer])
  // anypalette ships its own browserify Buffer, whose instances are Uint8Array
  // subclasses. Copy to a plain Uint8Array so h3 and Blob() both take it
  // without sniffing for a Buffer that does not exist on Cloudflare Workers.
  return typeof content === 'string' ? content : new Uint8Array(content)
}

/**
 * Build the export file for `format`.
 *
 * `data` may be the palette object or the JSON text of one (the legacy API
 * posts text). Whichever comes in, anypalette is handed the exact JSON text so
 * its parse stays byte-identical to the VPS's.
 */
export async function buildPaletteFile(
  format: string,
  data: PaletteData | string,
): Promise<PaletteFile> {
  if (!isPaletteFormat(format)) {
    throw new PaletteExportError(
      `Unsupported format "${String(format)}". Supported: ${SUPPORTED_FORMATS.join(', ')}.`,
    )
  }

  let parsed: PaletteData
  let text: string
  if (typeof data === 'string') {
    text = data
    try {
      parsed = JSON.parse(data)
    } catch {
      throw new PaletteExportError('`data` is not valid JSON.')
    }
  } else {
    parsed = data
    text = JSON.stringify(data)
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new PaletteExportError('`data` must be a palette object.')
  }

  const spec = PALETTE_FORMATS[format]
  const base = { extension: spec.extension, mimeType: spec.mimeType }

  if (!('writer' in spec)) {
    const content =
      format === 'SVG' ? buildPaletteSvg(collectGroups(parsed)) : buildVariables(parsed, format)
    return { ...base, content, binary: false }
  }

  const content = await buildWithAnyPalette(text, spec.writer)
  return { ...base, content, binary: typeof content !== 'string' }
}
